import { PageController } from '@client/components/_core/layout/PageController';
import { DocumentLibrary } from '@client/components/_shared/documents/DocumentLibrary';
import { History } from '@client/components/_ui/display/History';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { ConnectedRecordsPanel } from '@client/components/registers/ConnectedRecordsPanel';
import { RecordDetailsCard } from '@client/components/registers/RecordDetailsCard';
import { TaskList } from '@client/components/tasks/TaskList';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { trpc } from '@client/trpc/client';
import type { ShareableRouteContext } from '@client/types/router';
import { createFileRoute } from '@tanstack/react-router';
import { Skeleton, Tabs, Tag, Tooltip } from 'antd';
import { parseRegisterRouteId } from './parse-register-route-id';

// CUR-644 phases 3+5 — record detail: reference + title as the shared page title
// (REG-DL5, resolved in beforeLoad), Manage record / Library / History tabs
// (REG-DL2), per-field inline editing (REC-E*, plan D1), REG-DL3 field display,
// standard tasks panel (REC-T1) and file library (REC-D1/D2). Documents and tasks
// stay visible on a binned record (REG-B2) but every write path is locked while it
// is in the bin.

export const Route = createFileRoute('/console/$orgId/compliance/registers/$registerId/records/$recordId')({
    params: {
        parse({ recordId }) {
            return { recordId: parseRegisterRouteId(recordId) };
        },
    },
    beforeLoad({ context: { trpcUtils }, params: { registerId, recordId } }): ShareableRouteContext {
        // The record owns its own page title, the same way the register does one
        // level up. That also displaces the register's inherited `ui.title` —
        // route context flows down to every descendant match, so without a title
        // of its own this page would show the REGISTER's name as its heading.
        const record = () => trpcUtils.register.getRegisterRecord.ensureData({ registerId, recordId });

        return {
            // "#1 • Name" — the bullet is how the app separates an entity's
            // identifier from its name in a page title (obligations, MSPR).
            ui: {
                title: async () => {
                    const { ref, title } = await record();
                    return `#${ref} • ${title}`;
                },
            },
            // The record's reference, not a static "Record" on every one of them.
            // The trail is the only wayfinding back to the register now that the
            // page carries no back link.
            breadcrumb: {
                title: async () => `#${(await record()).ref}`,
            },
        };
    },
    component: RegisterRecordPage,
});

function RegisterRecordPage() {
    const { registerId, recordId } = Route.useParams();
    const { can } = usePermissions();
    // deletedAt is a real timestamp — local formatting is correct for it.
    const { formatDate } = useFormatter();
    const registerQuery = trpc.register.getRegister.useQuery({ id: registerId });
    const recordQuery = trpc.register.getRegisterRecord.useQuery({ registerId, recordId });

    const error = registerQuery.error ?? recordQuery.error;
    const isPending = registerQuery.isPending || recordQuery.isPending;
    const register = registerQuery.data;
    const record = recordQuery.data;

    const canUpdate = can('register:update') && record !== undefined && !record.isDeleted;

    return (
        <PageController route={Route}>
            {error ? (
                <InlineQueryError
                    error={error}
                    onRetry={() => {
                        void registerQuery.refetch();
                        void recordQuery.refetch();
                    }}
                    title="Couldn't load the record"
                />
            ) : isPending || !register || !record ? (
                <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
                <div className="flex flex-col gap-4">
                    {/* The reference and title are the page title (route `ui.title`),
                        so the only thing left to render here is the bin flag — and
                        only when it applies. No back link (the breadcrumb already
                        goes back), no reference chip (it is in the title, the way
                        every other entity does it), and no page-level delete: no
                        other entity detail page has one, and a record is deleted
                        from its list row, which is where REG-B6 puts the bin. */}
                    {record.isDeleted && (
                        <Tooltip
                            // Hover alone strands keyboard users on a focusable chip
                            // that never reveals its content (WCAG 1.4.13).
                            trigger={['hover', 'focus']}
                            title={`Deleted by ${record.DeletedBy?.fullName ?? 'unknown'} on ${
                                record.deletedAt ? formatDate(record.deletedAt) : 'unknown'
                            }. Reason: ${record.deletionReason ?? '-'}`}
                        >
                            {/* Focusable: the who/when/why lives only in the
                                tooltip, so it must be keyboard-reachable. */}
                            <Tag color="red" variant="outlined" tabIndex={0} className="self-start">
                                Deleted
                            </Tag>
                        </Tooltip>
                    )}

                    <Tabs
                        size="small"
                        defaultActiveKey="manage"
                        items={[
                            {
                                key: 'manage',
                                label: 'Manage record',
                                children: (
                                    // Left: record details; right: Connected records (REC-L*),
                                    // matching the prototype's Manage-tab split.
                                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
                                        <div className="flex flex-col gap-4 lg:col-span-3">
                                            <RecordDetailsCard
                                                register={register}
                                                record={record}
                                                canEdit={canUpdate}
                                            />
                                            {/* REC-T1: the app's standard tasks panel — ordinary Task
                                                rows with relatedEntity = register record. */}
                                            <TaskList
                                                relatedEntity="registerRecord"
                                                relatedEntityId={recordId}
                                                size="small"
                                                hideAddTask={!canUpdate}
                                            />
                                        </div>
                                        <div className="lg:col-span-2">
                                            <ConnectedRecordsPanel
                                                registerId={registerId}
                                                recordId={recordId}
                                                canEdit={canUpdate}
                                            />
                                        </div>
                                    </div>
                                ),
                            },
                            {
                                key: 'library',
                                label: 'Library',
                                children: (
                                    // REG-DL2/REC-D1: the standard file library — drag-drop upload,
                                    // Show deleted, delete-with-reason — nothing register-specific.
                                    <DocumentLibrary
                                        entity="registerRecord"
                                        entityId={recordId}
                                        disableUploading={!canUpdate}
                                        // A binned record is read-only except Restore (REG-B6) and
                                        // its documents travel with it untouched (REG-B2), so the
                                        // whole library freezes — not just uploads.
                                        readOnly={!canUpdate}
                                    />
                                ),
                            },
                            {
                                key: 'history',
                                label: 'History',
                                // No width wrapper: History is a full-width table
                                // with its own search, filters and pagination, and
                                // every other caller (incidents, claims, complaints,
                                // risk, MSPR, tasks, training) renders it bare. A
                                // max-width here pinned it to 896px inside a 1609px
                                // tab panel, so the Detail column truncated while
                                // half the row sat empty.
                                children: <History idValue={recordId} idPropertyName="registerRecordId" />,
                            },
                        ]}
                    />
                </div>
            )}
        </PageController>
    );
}
